import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {Observable, of} from 'rxjs';
import {catchError, map, mergeMap, take, tap} from 'rxjs/operators';
import {AppState} from '../../../../state/app.state';
import {ReactionApiModel} from '../../../../api/models/reaction.api.model';
import {SocialListType} from './social-list-type.type';
import {selectCombinedReactionList, selectReactionEntities, selectReactionListPage} from '../../../../state/reaction-list/reaction-list.selectors';
import {AddMany, AddOne, DeleteOne} from '../../../../state/reaction/reaction.actions';
import {
  AddReactionToList,
  LoadPage,
  LoadPageError,
  LoadPageSuccess,
  RemoveReactionFromList,
  SeedPage
} from '../../../../state/reaction-list/reaction-list.actions';

export interface ReactionListCacheTarget {
  listType: SocialListType;
  subjectId: string;
}

export interface ReactionListCachedPageResult {
  reactions: ReactionApiModel[];
  hasMore: boolean;
  loadedPageCount: number;
}

@Injectable({providedIn: 'root'})
export class ReactionListCacheFacade {
  public loadPage$(
    target: ReactionListCacheTarget,
    page: number,
    pageSize: number,
    fetchPage: (() => Observable<ReactionApiModel[]>) | null,
    initialData?: ReactionApiModel[]
  ): Observable<ReactionListCachedPageResult> {
    if (initialData?.length) {
      this.seedFromInitialData(target, initialData, pageSize);
    }

    if (page === 1) {
      return this.store.select(selectCombinedReactionList(target.listType, target.subjectId)).pipe(
        take(1),
        mergeMap((combined) => {
          if (combined.reactions.length || combined.loadedPageCount > 0) {
            return of({reactions: combined.reactions, hasMore: combined.hasMore, loadedPageCount: Math.max(combined.loadedPageCount, 1)});
          }

          return this.fetchAndCachePage$(target, page, pageSize, fetchPage);
        })
      );
    }

    return this.store.select(selectReactionListPage(target.listType, target.subjectId, page)).pipe(
      take(1),
      mergeMap((pageState) => {
        if (pageState?.status === 'success') {
          return this.store.select(selectReactionEntities).pipe(
            take(1),
            map((entities) => ({
              reactions: (pageState.ids || []).map((id) => entities[id]).filter(Boolean),
              hasMore: pageState.hasMore,
              loadedPageCount: 1
            }))
          );
        }

        return this.fetchAndCachePage$(target, page, pageSize, fetchPage);
      })
    );
  }

  public seedFromInitialData(target: ReactionListCacheTarget, reactions: ReactionApiModel[], pageSize: number): void {
    this.store.dispatch(new AddMany(reactions));

    for (let page = 1; ; page += 1) {
      const start = (page - 1) * pageSize;
      const chunk = reactions.slice(start, start + pageSize);
      if (!chunk.length) {
        break;
      }

      this.store.dispatch(new SeedPage(
        target.listType,
        target.subjectId,
        page,
        chunk,
        start + chunk.length < reactions.length
      ));
    }
  }

  public addReactionToTargets(reaction: ReactionApiModel, targets: ReactionListCacheTarget[]): void {
    this.store.dispatch(new AddOne(reaction));
    targets.forEach((target) => {
      this.store.dispatch(new AddReactionToList(target.listType, target.subjectId, reaction));
    });
  }

  public removeReactionFromTargets(reactionId: string, targets: ReactionListCacheTarget[]): void {
    this.store.dispatch(new DeleteOne(reactionId));
    targets.forEach((target) => {
      this.store.dispatch(new RemoveReactionFromList(target.listType, target.subjectId, reactionId));
    });
  }

  private fetchAndCachePage$(
    target: ReactionListCacheTarget,
    page: number,
    pageSize: number,
    fetchPage: (() => Observable<ReactionApiModel[]>) | null
  ): Observable<ReactionListCachedPageResult> {
    if (!fetchPage) {
      return of({reactions: [], hasMore: false, loadedPageCount: 1});
    }

    this.store.dispatch(new LoadPage(target.listType, target.subjectId, page));

    return fetchPage().pipe(
      tap((reactions) => {
        this.store.dispatch(new AddMany(reactions));
        this.store.dispatch(new LoadPageSuccess(
          target.listType,
          target.subjectId,
          page,
          reactions,
          reactions.length >= pageSize
        ));
      }),
      map((reactions) => ({reactions, hasMore: reactions.length >= pageSize, loadedPageCount: 1})),
      catchError(() => {
        this.store.dispatch(new LoadPageError(target.listType, target.subjectId, page));
        return of({reactions: [], hasMore: false, loadedPageCount: 1});
      })
    );
  }

  constructor(
    private readonly store: Store<AppState>,
  ) {
  }
}
